import React, { useEffect, useState, useCallback } from "react";
import API from "../../utils/api";
import { useAuth } from "../../App";
import toast from "react-hot-toast";

export default function Approvals() {
  const { user } = useAuth();
  // SSO keeps the employee's base HRMS role in `role` and supplies the
  // app-specific authority in app_role_overrides.  Navigation and badges use
  // this effective Expense role, so this screen must do the same; otherwise a
  // delegated approver sees a badge count but the matching sections never load.
  const expenseRole = user?.app_role_overrides?.expense
    || user?.app_role_overrides?.expenses
    || user?.role;
  const isAdmin   = ["admin", "superadmin"].includes(expenseRole);
  const isManager = expenseRole === "manager";
  const isTeamLeader = expenseRole === "team_leader";
  const canApproveAdvances = isManager || isAdmin || isTeamLeader;

  // ── Advance Requests (manager) ─────────────────────────────────────────────
  const [advanceRequests, setAdvanceRequests] = useState([]);
  const [loadingAdvances, setLoadingAdvances] = useState(true);
  const [advDeclineMode, setAdvDeclineMode] = useState(null);
  const [advDeclineNote, setAdvDeclineNote] = useState("");
  const [advApproveMode, setAdvApproveMode] = useState(null);   // req.id when approve panel is open
  const [advApprovedAmount, setAdvApprovedAmount] = useState(""); // manager's confirmed/modified amount
  const [advApproveNote, setAdvApproveNote] = useState("");
  const [advProcessingId, setAdvProcessingId] = useState(null);
  // Projections per trip_id: { [trip_id]: { rows: [], loading: false } }
  const [tripProjections, setTripProjections] = useState({});
  const [expandedAdvance, setExpandedAdvance] = useState(null);

  // ── Overage Approvals (trips where actuals > projected) ────────────────────
  const [overageTrips, setOverageTrips] = useState([]);
  const [loadingOverage, setLoadingOverage] = useState(true);
  const [overageProcessingId, setOverageProcessingId] = useState(null);
  const [overageNoteMode, setOverageNoteMode] = useState(null);
  const [overageNote, setOverageNote] = useState("");

  // ── BF Override Requests (manager) ────────────────────────────────────────
  const [bfOverrides, setBfOverrides] = useState([]);
  const [loadingBf, setLoadingBf] = useState(true);
  const [bfDeclineMode, setBfDeclineMode] = useState(null);
  const [bfDeclineNote, setBfDeclineNote] = useState("");
  const [bfProcessingId, setBfProcessingId] = useState(null);

  // ── Cash Bill Approvals ────────────────────────────────────────────────────
  const [cashApprovals, setCashApprovals] = useState([]);
  const [loadingCash, setLoadingCash] = useState(true);
  const [cashDeclineMode, setCashDeclineMode] = useState(null);
  const [cashDeclineNote, setCashDeclineNote] = useState("");
  const [cashProcessingId, setCashProcessingId] = useState(null);

  // ── Bulk selection state ───────────────────────────────────────────────────
  const [selectedAdv, setSelectedAdv] = useState(new Set());
  const [bulkAdvDecline, setBulkAdvDecline] = useState(false);
  const [bulkAdvNote, setBulkAdvNote]   = useState("");
  const [bulkAdvBusy, setBulkAdvBusy]   = useState(false);

  const [selectedBf, setSelectedBf] = useState(new Set());
  const [bulkBfDecline, setBulkBfDecline] = useState(false);
  const [bulkBfNote, setBulkBfNote]     = useState("");
  const [bulkBfBusy, setBulkBfBusy]     = useState(false);

  const [selectedCash, setSelectedCash] = useState(new Set());
  const [bulkCashDecline, setBulkCashDecline] = useState(false);
  const [bulkCashNote, setBulkCashNote]   = useState("");
  const [bulkCashBusy, setBulkCashBusy]   = useState(false);
  const [viewingBillId, setViewingBillId] = useState(null);

  const loadAdvances = useCallback(() => {
    if (!canApproveAdvances) return;
    setLoadingAdvances(true);
    API.get("/advance-requests/pending")
      .then(r => { setAdvanceRequests(r.data || []); setSelectedAdv(new Set()); })
      .catch(() => setAdvanceRequests([]))
      .finally(() => setLoadingAdvances(false));
  }, [canApproveAdvances]);

  const loadOverageTrips = useCallback(() => {
    if (!isManager && !isAdmin && !isTeamLeader) return;
    setLoadingOverage(true);
    API.get("/trips/projections/summary")
      .then(r => { setOverageTrips((r.data?.overage_trips || []).filter(t => !t.overage_approved)); })
      .catch(() => setOverageTrips([]))
      .finally(() => setLoadingOverage(false));
  }, [isManager, isAdmin, isTeamLeader]);

  const fetchTripProjections = useCallback(async (tripId) => {
    if (tripProjections[tripId]) return; // already loaded
    setTripProjections(prev => ({ ...prev, [tripId]: { rows: [], loading: true } }));
    try {
      const r = await API.get(`/trips/${tripId}/projections`);
      setTripProjections(prev => ({ ...prev, [tripId]: { rows: r.data.projections || [], loading: false, summary: r.data.summary } }));
    } catch {
      setTripProjections(prev => ({ ...prev, [tripId]: { rows: [], loading: false } }));
    }
  }, [tripProjections]);

  const loadBfOverrides = useCallback(() => {
    if (!isManager && !isAdmin && !isTeamLeader) return;
    setLoadingBf(true);
    const endpoint = isAdmin
      ? "/expenses/bf-override-requests/finance-pending"
      : "/expenses/bf-override-requests/pending";
    API.get(endpoint)
      .then(r => { setBfOverrides(r.data || []); setSelectedBf(new Set()); })
      .catch(() => setBfOverrides([]))
      .finally(() => setLoadingBf(false));
  }, [isManager, isAdmin, isTeamLeader]);

  const loadCashApprovals = useCallback(() => {
    setLoadingCash(true);
    API.get(`/expenses/cash-bill-approvals/pending${isAdmin ? "?view=all" : ""}`)
      .then(r => { setCashApprovals(r.data || []); setSelectedCash(new Set()); })
      .catch(() => setCashApprovals([]))
      .finally(() => setLoadingCash(false));
  }, [isAdmin]);

  useEffect(() => {
    loadAdvances();
    loadBfOverrides();
    loadCashApprovals();
    loadOverageTrips();
  }, [loadAdvances, loadBfOverrides, loadCashApprovals, loadOverageTrips]);

  // ── Advance handlers ───────────────────────────────────────────────────────
  const handleAdvApprove = async (req) => {
    setAdvProcessingId(req.id);
    try {
      const finalAmount = parseFloat(advApprovedAmount) || parseFloat(req.amount);
      await API.post(`/advance-requests/${req.id}/approve`, {
        manager_notes: advApproveNote || "",
        approved_amount: finalAmount,
      });
      const modified = Math.abs(finalAmount - parseFloat(req.amount)) > 0.01;
      toast.success(modified
        ? `✅ Approved ₹${finalAmount.toLocaleString("en-IN")} (modified from ₹${parseFloat(req.amount).toLocaleString("en-IN")}).`
        : `✅ Advance of ₹${finalAmount.toLocaleString("en-IN")} approved.`
      );
      setAdvanceRequests(prev => prev.filter(r => r.id !== req.id));
      setSelectedAdv(prev => { const s = new Set(prev); s.delete(req.id); return s; });
      setAdvApproveMode(null); setAdvApprovedAmount(""); setAdvApproveNote("");
    } catch { toast.error("Failed to approve"); }
    finally { setAdvProcessingId(null); }
  };

  const handleAdvDecline = async (req) => {
    setAdvProcessingId(req.id);
    try {
      await API.post(`/advance-requests/${req.id}/decline`, { manager_notes: advDeclineNote });
      toast.success(`Advance request from ${req.employee_name} declined.`);
      setAdvanceRequests(prev => prev.filter(r => r.id !== req.id));
      setSelectedAdv(prev => { const s = new Set(prev); s.delete(req.id); return s; });
      setAdvDeclineMode(null);
      setAdvDeclineNote("");
    } catch { toast.error("Failed to decline"); }
    finally { setAdvProcessingId(null); }
  };

  // Bulk advance approve
  const handleBulkAdvApprove = async () => {
    if (!selectedAdv.size) return;
    setBulkAdvBusy(true);
    const ids = [...selectedAdv];
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await API.post(`/advance-requests/${id}/approve`, { manager_notes: "" });
        ok++;
        setAdvanceRequests(prev => prev.filter(r => r.id !== id));
      } catch { fail++; }
    }
    setBulkAdvBusy(false);
    setSelectedAdv(new Set());
    if (ok > 0) toast.success(`✅ ${ok} advance request${ok > 1 ? "s" : ""} approved.`);
    if (fail > 0) toast.error(`${fail} failed — please retry.`);
  };

  // Bulk advance decline
  const handleBulkAdvDecline = async () => {
    if (!selectedAdv.size) return;
    setBulkAdvBusy(true);
    const ids = [...selectedAdv];
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await API.post(`/advance-requests/${id}/decline`, { manager_notes: bulkAdvNote });
        ok++;
        setAdvanceRequests(prev => prev.filter(r => r.id !== id));
      } catch { fail++; }
    }
    setBulkAdvBusy(false);
    setSelectedAdv(new Set());
    setBulkAdvDecline(false);
    setBulkAdvNote("");
    if (ok > 0) toast.success(`${ok} advance request${ok > 1 ? "s" : ""} declined.`);
    if (fail > 0) toast.error(`${fail} failed — please retry.`);
  };

  // ── BF override handlers ──────────────────────────────────────────────────
  const handleBfApprove = async (req) => {
    setBfProcessingId(req.id);
    try {
      const endpoint = isAdmin
        ? `/expenses/bf-override-requests/${req.id}/finance-approve`
        : `/expenses/bf-override-requests/${req.id}/approve`;
      await API.post(endpoint);
      toast.success(`✅ BF override approved for ${req.employee_name}.`);
      setBfOverrides(prev => prev.filter(r => r.id !== req.id));
      setSelectedBf(prev => { const s = new Set(prev); s.delete(req.id); return s; });
    } catch { toast.error("Failed to approve"); }
    finally { setBfProcessingId(null); }
  };

  const handleBfDecline = async (req) => {
    setBfProcessingId(req.id);
    try {
      const endpoint = isAdmin
        ? `/expenses/bf-override-requests/${req.id}/finance-decline`
        : `/expenses/bf-override-requests/${req.id}/decline`;
      await API.post(endpoint, { notes: bfDeclineNote });
      toast.success("BF override declined.");
      setBfOverrides(prev => prev.filter(r => r.id !== req.id));
      setSelectedBf(prev => { const s = new Set(prev); s.delete(req.id); return s; });
      setBfDeclineMode(null);
      setBfDeclineNote("");
    } catch { toast.error("Failed to decline"); }
    finally { setBfProcessingId(null); }
  };

  // Bulk BF approve
  const handleBulkBfApprove = async () => {
    if (!selectedBf.size) return;
    setBulkBfBusy(true);
    const ids = [...selectedBf];
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        const endpoint = isAdmin
          ? `/expenses/bf-override-requests/${id}/finance-approve`
          : `/expenses/bf-override-requests/${id}/approve`;
        await API.post(endpoint);
        ok++;
        setBfOverrides(prev => prev.filter(r => r.id !== id));
      } catch { fail++; }
    }
    setBulkBfBusy(false);
    setSelectedBf(new Set());
    if (ok > 0) toast.success(`✅ ${ok} BF override${ok > 1 ? "s" : ""} approved.`);
    if (fail > 0) toast.error(`${fail} failed — please retry.`);
  };

  // Bulk BF decline
  const handleBulkBfDecline = async () => {
    if (!selectedBf.size) return;
    setBulkBfBusy(true);
    const ids = [...selectedBf];
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        const endpoint = isAdmin
          ? `/expenses/bf-override-requests/${id}/finance-decline`
          : `/expenses/bf-override-requests/${id}/decline`;
        await API.post(endpoint, { notes: bulkBfNote });
        ok++;
        setBfOverrides(prev => prev.filter(r => r.id !== id));
      } catch { fail++; }
    }
    setBulkBfBusy(false);
    setSelectedBf(new Set());
    setBulkBfDecline(false);
    setBulkBfNote("");
    if (ok > 0) toast.success(`${ok} BF override${ok > 1 ? "s" : ""} declined.`);
    if (fail > 0) toast.error(`${fail} failed — please retry.`);
  };

  // ── Cash bill handlers ─────────────────────────────────────────────────────
  const handleCashApprove = async (req) => {
    setCashProcessingId(req.id);
    try {
      const managerStage = isAdmin && req.manager_status === "pending";
      const endpoint = managerStage
        ? `/expenses/cash-bill-approvals/${req.id}/approve`
        : (isAdmin
          ? `/expenses/cash-bill-approvals/${req.id}/finance-approve`
          : `/expenses/cash-bill-approvals/${req.id}/approve`);
      await API.post(endpoint);
      toast.success(managerStage
        ? "✅ Manager stage approved on behalf of the unavailable manager. Finance approval is now pending."
        : `✅ Cash bill of ₹${parseFloat(req.amount).toLocaleString("en-IN")} approved.`);
      if (managerStage) loadCashApprovals();
      else setCashApprovals(prev => prev.filter(r => r.id !== req.id));
      setSelectedCash(prev => { const s = new Set(prev); s.delete(req.id); return s; });
    } catch { toast.error("Failed to approve"); }
    finally { setCashProcessingId(null); }
  };

  const handleCashDecline = async (req) => {
    setCashProcessingId(req.id);
    try {
      const endpoint = isAdmin && req.manager_status === "pending"
        ? `/expenses/cash-bill-approvals/${req.id}/decline`
        : (isAdmin
          ? `/expenses/cash-bill-approvals/${req.id}/finance-decline`
          : `/expenses/cash-bill-approvals/${req.id}/decline`);
      await API.post(endpoint, { notes: cashDeclineNote });
      toast.success("Cash bill declined.");
      setCashApprovals(prev => prev.filter(r => r.id !== req.id));
      setSelectedCash(prev => { const s = new Set(prev); s.delete(req.id); return s; });
      setCashDeclineMode(null);
      setCashDeclineNote("");
    } catch { toast.error("Failed to decline"); }
    finally { setCashProcessingId(null); }
  };

  // Bulk cash approve
  const handleBulkCashApprove = async () => {
    if (!selectedCash.size) return;
    setBulkCashBusy(true);
    const ids = [...selectedCash];
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        const item = cashApprovals.find(r => r.id === id);
        const endpoint = isAdmin && item?.manager_status === "pending"
          ? `/expenses/cash-bill-approvals/${id}/approve`
          : (isAdmin
            ? `/expenses/cash-bill-approvals/${id}/finance-approve`
            : `/expenses/cash-bill-approvals/${id}/approve`);
        await API.post(endpoint);
        ok++;
        setCashApprovals(prev => prev.filter(r => r.id !== id));
      } catch { fail++; }
    }
    setBulkCashBusy(false);
    setSelectedCash(new Set());
    if (ok > 0) { toast.success(`✅ ${ok} cash bill${ok > 1 ? "s" : ""} approved.`); loadCashApprovals(); }
    if (fail > 0) toast.error(`${fail} failed — please retry.`);
  };

  // Bulk cash decline
  const handleBulkCashDecline = async () => {
    if (!selectedCash.size) return;
    setBulkCashBusy(true);
    const ids = [...selectedCash];
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        const item = cashApprovals.find(r => r.id === id);
        const endpoint = isAdmin && item?.manager_status === "pending"
          ? `/expenses/cash-bill-approvals/${id}/decline`
          : (isAdmin
            ? `/expenses/cash-bill-approvals/${id}/finance-decline`
            : `/expenses/cash-bill-approvals/${id}/decline`);
        await API.post(endpoint, { notes: bulkCashNote });
        ok++;
        setCashApprovals(prev => prev.filter(r => r.id !== id));
      } catch { fail++; }
    }
    setBulkCashBusy(false);
    setSelectedCash(new Set());
    setBulkCashDecline(false);
    setBulkCashNote("");
    if (ok > 0) toast.success(`${ok} cash bill${ok > 1 ? "s" : ""} declined.`);
    if (fail > 0) toast.error(`${fail} failed — please retry.`);
  };

  // ── Overage approval handler ───────────────────────────────────────────────
  const handleApproveOverage = async (trip) => {
    setOverageProcessingId(trip.id);
    try {
      await API.post(`/trips/${trip.id}/approve-overage`, { notes: overageNote });
      toast.success(`✅ Overage approved for "${trip.title}"`);
      setOverageTrips(prev => prev.filter(t => t.id !== trip.id));
      setOverageNoteMode(null);
      setOverageNote("");
    } catch { toast.error("Failed to approve overage"); }
    finally { setOverageProcessingId(null); }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const fmtDate = (d) => d ? new Date(d).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";
  const fmtINR  = (v) => `₹${parseFloat(v||0).toLocaleString("en-IN")}`;
  const hasBillAttached = (req) => Boolean(Number(req?.has_bill));

  const viewBill = async (expenseId) => {
    if (!expenseId) { toast.error("No expense linked to this request"); return; }
    setViewingBillId(expenseId);
    try {
      const response = await API.get(`/expenses/${expenseId}/bill-file`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) toast.error("Allow popups to view bills");
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open bill. It may not be attached yet.");
    } finally {
      setViewingBillId(null);
    }
  };

  const ViewBillButton = ({ req }) => {
    if (!hasBillAttached(req) || !req.expense_id) {
      return (
        <span style={{ fontSize: 12, color: "#9ca3af", alignSelf: "center", padding: "10px 0" }}>
          No bill attached
        </span>
      );
    }
    const busy = viewingBillId === req.expense_id;
    return (
      <button type="button" disabled={busy} onClick={() => viewBill(req.expense_id)}
        style={{
          background: "white", color: "#0f766e", border: "2px solid #0d9488",
          borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700,
          cursor: busy ? "not-allowed" : "pointer",
        }}>
        {busy ? "Opening..." : "📎 View Bill"}
      </button>
    );
  };

  const hoursAgo = (dateStr) => {
    if (!dateStr) return null;
    const diff = Date.now() - new Date(dateStr).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return "just now";
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h ago`;
  };

  // ── Bulk action bar component (reused per section) ─────────────────────────
  const BulkBar = ({
    selected, allItems, onSelectAll, onClearAll,
    onBulkApprove, onBulkDeclineClick,
    showDeclineBox, declineNote, onDeclineNoteChange,
    onBulkDeclineConfirm, onDeclineCancel,
    busy, approveLabel, declineLabel,
    borderColor
  }) => {
    if (selected.size === 0) return null;
    return (
      <div style={{
        background: "#1e293b", borderRadius: 10, padding: "12px 18px",
        marginBottom: 14, border: `1px solid ${borderColor || "#334155"}`,
        display: "flex", flexDirection: "column", gap: 10
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>
            ☑️ {selected.size} of {allItems.length} selected
          </span>
          <button onClick={onSelectAll} disabled={busy}
            style={{ fontSize: 12, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
            Select all {allItems.length}
          </button>
          <button onClick={onClearAll} disabled={busy}
            style={{ fontSize: 12, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
            Clear
          </button>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={onBulkApprove} disabled={busy}
              style={{ background: busy ? "#374151" : "#059669", color: "white", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 13, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer" }}>
              {busy ? "Processing..." : `✅ ${approveLabel || "Approve"} (${selected.size})`}
            </button>
            {!showDeclineBox && (
              <button onClick={onBulkDeclineClick} disabled={busy}
                style={{ background: "white", color: "#DC2626", border: "2px solid #DC2626", borderRadius: 7, padding: "7px 16px", fontSize: 13, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer" }}>
                ❌ {declineLabel || "Decline"} ({selected.size})
              </button>
            )}
          </div>
        </div>
        {showDeclineBox && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#cbd5e1", display: "block", marginBottom: 6 }}>
              Decline reason for all {selected.size} selected <span style={{ color: "#64748b", fontWeight: 400 }}>(optional, shared)</span>
            </label>
            <textarea value={declineNote} onChange={e => onDeclineNoteChange(e.target.value)}
              placeholder="e.g. Budget not approved for this period."
              rows={2} style={{ width: "100%", padding: "8px 12px", borderRadius: 7, border: "1px solid #334155", background: "#0f172a", color: "#f1f5f9", fontSize: 13, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button onClick={onBulkDeclineConfirm} disabled={busy}
                style={{ background: busy ? "#374151" : "#DC2626", color: "white", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 13, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer" }}>
                {busy ? "Declining..." : `Confirm Decline (${selected.size})`}
              </button>
              <button onClick={onDeclineCancel} disabled={busy}
                style={{ background: "none", color: "#94a3b8", border: "1px solid #334155", borderRadius: 7, padding: "7px 14px", fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Rows the current user is actually authorized to act on (canApprove === false
  // means a designated company approver exists elsewhere, e.g. Adrian for Ebek —
  // the item stays visible for read-only tracking but action buttons are hidden).
  const approvableAdvances = advanceRequests.filter(r => r.canApprove !== false);
  const approvableBf = bfOverrides.filter(r => r.canApprove !== false);
  const approvableCash = cashApprovals.filter(r => r.canApprove !== false);

  const totalPending = (canApproveAdvances ? advanceRequests.length : 0) + approvableBf.length + approvableCash.length + overageTrips.length;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 className="page-title">
          Approvals
          {totalPending > 0 && (
            <span style={{
              marginLeft: 12, background: "#DC2626", color: "white",
              borderRadius: 20, padding: "3px 12px", fontSize: 14, fontWeight: 700, verticalAlign: "middle"
            }}>
              {totalPending} pending
            </span>
          )}
        </h1>
        <p className="page-subtitle">
          {isAdmin ? "Finance approvals for cash vouchers over the ₹500 daily limit" : "Manage advance requests and cash voucher approvals from your team"}
        </p>
      </div>

      {/* ── Advance Requests (manager | Ebek admin | superadmin) ───────────────── */}
      {canApproveAdvances && (
        <div className="card" style={{ marginBottom: 24, border: "2px solid #FCD34D", background: "#FFFBEB" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 24 }}>💰</span>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#92400e", margin: 0 }}>
                Advance Requests
                {advanceRequests.length > 0 && (
                  <span style={{ marginLeft: 8, background: "#F59E0B", color: "white", borderRadius: 20, padding: "2px 10px", fontSize: 13 }}>
                    {advanceRequests.length}
                  </span>
                )}
              </h3>
              <p style={{ fontSize: 12, color: "#b45309", margin: 0, marginTop: 2 }}>
                {isManager ? "Team members waiting for advance approval" : "Pending advance requests for Ebek employees"}
              </p>
            </div>
            {/* Select All toggle */}
            {approvableAdvances.length > 1 && !loadingAdvances && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#92400e", cursor: "pointer", userSelect: "none" }}>
                <input type="checkbox"
                  checked={selectedAdv.size === approvableAdvances.length && approvableAdvances.length > 0}
                  onChange={e => setSelectedAdv(e.target.checked ? new Set(approvableAdvances.map(r => r.id)) : new Set())}
                  style={{ width: 16, height: 16, cursor: "pointer" }} />
                Select All
              </label>
            )}
          </div>

          {loadingAdvances ? (
            <div style={{ textAlign: "center", padding: "20px 0", color: "#9ca3af" }}>Loading...</div>
          ) : advanceRequests.length === 0 ? (
            <div style={{ textAlign: "center", padding: "16px 0", color: "#6b7280", fontSize: 14 }}>✅ No pending advance requests</div>
          ) : (
            <>
              <BulkBar
                selected={selectedAdv} allItems={approvableAdvances}
                onSelectAll={() => setSelectedAdv(new Set(approvableAdvances.map(r => r.id)))}
                onClearAll={() => { setSelectedAdv(new Set()); setBulkAdvDecline(false); setBulkAdvNote(""); }}
                onBulkApprove={handleBulkAdvApprove}
                onBulkDeclineClick={() => setBulkAdvDecline(true)}
                showDeclineBox={bulkAdvDecline}
                declineNote={bulkAdvNote} onDeclineNoteChange={setBulkAdvNote}
                onBulkDeclineConfirm={handleBulkAdvDecline}
                onDeclineCancel={() => { setBulkAdvDecline(false); setBulkAdvNote(""); }}
                busy={bulkAdvBusy} borderColor="#FCD34D"
              />
              <div style={{ display: "grid", gap: 14 }}>
                {advanceRequests.map(req => {
                  const isUrgent = req.hours_pending && req.hours_pending > 24;
                  const isDeclineOpen = advDeclineMode === req.id;
                  const isProcessing = advProcessingId === req.id;
                  const isChecked = selectedAdv.has(req.id);
                  return (
                    <div key={req.id} style={{
                      background: "white", borderRadius: 12, padding: "18px 20px",
                      border: isChecked ? "2px solid #F59E0B" : isUrgent ? "2px solid #FCA5A5" : "1px solid #FDE68A",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                      transition: "border-color 0.15s"
                    }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                          {/* Checkbox */}
                          {req.canApprove !== false ? (
                            <input type="checkbox" checked={isChecked}
                              onChange={e => {
                                const s = new Set(selectedAdv);
                                e.target.checked ? s.add(req.id) : s.delete(req.id);
                                setSelectedAdv(s);
                              }}
                              style={{ width: 17, height: 17, marginTop: 3, cursor: "pointer", accentColor: "#F59E0B" }} />
                          ) : (
                            <div style={{ width: 17, marginTop: 3 }} />
                          )}
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 15, fontWeight: 800, color: "#1f2937" }}>{req.employee_name}</span>
                              {isUrgent ? (
                                <span style={{ fontSize: 11, background: "#FEE2E2", color: "#DC2626", borderRadius: 8, padding: "2px 8px", fontWeight: 700 }}>⚠️ Waiting long</span>
                              ) : (
                                <span style={{ fontSize: 11, background: "#FEF3C7", color: "#92400e", borderRadius: 8, padding: "2px 8px", fontWeight: 600 }}>🕐 {hoursAgo(req.requested_at)}</span>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 3 }}>{req.employee_email}</div>
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>📅 Requested: <strong>{fmtDate(req.requested_at)}</strong></div>
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Amount</div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: "#d97706" }}>{fmtINR(req.amount)}</div>
                        </div>
                      </div>

                      <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "10px 14px", marginBottom: 14, display: "flex", flexWrap: "wrap", gap: 16 }}>
                        <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Trip</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937", marginTop: 1 }}>{req.trip_title || "—"}</div></div>
                        {req.destination && <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Destination</div>
                          <div style={{ fontSize: 14, color: "#374151", marginTop: 1 }}>📍 {req.destination}</div></div>}
                        {req.purpose && <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Purpose</div>
                          <div style={{ fontSize: 13, color: "#374151", marginTop: 1 }}>{req.purpose}</div></div>}
                        {/* Show assigned manager name for admin view */}
                        {!isManager && req.manager_name && (
                          <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Assigned Manager</div>
                            <div style={{ fontSize: 13, color: "#374151", marginTop: 1 }}>👤 {req.manager_name}</div></div>
                        )}
                      </div>

                      {req.notes && (
                        <div style={{ background: "#EFF6FF", borderRadius: 8, padding: "8px 12px", marginBottom: 14, fontSize: 13, color: "#1e40af" }}>
                          💬 <em>"{req.notes}"</em>
                        </div>
                      )}

                      {/* ── Expense Projection toggle ── */}
                      <div style={{ marginBottom: 12 }}>
                        <button
                          onClick={() => {
                            const isOpen = expandedAdvance === req.id;
                            setExpandedAdvance(isOpen ? null : req.id);
                            if (!isOpen) fetchTripProjections(req.trip_id);
                          }}
                          style={{
                            background: "none", border: "1.5px solid #E5E7EB", borderRadius: 8,
                            padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                            color: "#6b7280", display: "flex", alignItems: "center", gap: 6,
                          }}
                        >
                          📋 {expandedAdvance === req.id ? "Hide" : "View"} Expense Projection
                          <span style={{ fontSize: 10 }}>{expandedAdvance === req.id ? "▲" : "▼"}</span>
                        </button>

                        {expandedAdvance === req.id && (() => {
                          const proj = tripProjections[req.trip_id];
                          if (!proj || proj.loading) {
                            return (
                              <div style={{ marginTop: 10, padding: "12px 14px", background: "#F9FAFB", borderRadius: 8, fontSize: 13, color: "#9ca3af" }}>
                                ⏳ Loading projections...
                              </div>
                            );
                          }
                          if (!proj.rows || proj.rows.length === 0) {
                            return (
                              <div style={{ marginTop: 10, padding: "12px 14px", background: "#FEF3C7", borderRadius: 8, fontSize: 13, color: "#92400e" }}>
                                ℹ️ No expense projection submitted with this request.
                              </div>
                            );
                          }
                          const projTotal = proj.rows.reduce((s, r) => s + parseFloat(r.projected_amount || 0), 0);
                          const ICONS = { Travel:"🚂", Hotel:"🏨", Food:"🍽️", Fuel:"⛽", Courier:"📦", Stationeries:"📝", Miscellaneous:"📌" };
                          return (
                            <div style={{ marginTop: 10, background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 10, padding: "12px 14px" }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#065F46", textTransform: "uppercase", marginBottom: 10 }}>
                                Expected Expenses
                              </div>
                              {proj.rows.map(r => (
                                <div key={r.category} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
                                  <span style={{ color: "#374151" }}>{ICONS[r.category] || "📌"} {r.category}</span>
                                  <span style={{ fontWeight: 700, color: "#059669" }}>₹{parseFloat(r.projected_amount).toLocaleString("en-IN")}</span>
                                </div>
                              ))}
                              <div style={{ borderTop: "1px solid #86EFAC", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 800 }}>
                                <span style={{ color: "#065F46" }}>Projected Total</span>
                                <span style={{ color: "#059669" }}>₹{projTotal.toLocaleString("en-IN")}</span>
                              </div>
                              {parseFloat(req.amount) > projTotal * 1.1 && (
                                <div style={{ marginTop: 8, padding: "6px 10px", background: "#FEF3C7", borderRadius: 6, fontSize: 12, color: "#92400e" }}>
                                  ⚠️ Advance requested (₹{parseFloat(req.amount).toLocaleString("en-IN")}) is {((parseFloat(req.amount) / projTotal - 1) * 100).toFixed(0)}% above projected total
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* ── Approve panel (with optional amount modification) ── */}
                      {advApproveMode === req.id && (
                        <div style={{ marginBottom: 12, background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 10, padding: "14px 16px" }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#065F46", marginBottom: 10 }}>
                            ✅ Confirm Approval
                          </div>
                          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
                            Approved Amount (₹) <span style={{ color: "#9ca3af", fontWeight: 400 }}>— change if approving a different amount</span>
                          </label>
                          <input
                            type="number" min="1" step="1"
                            value={advApprovedAmount}
                            onChange={e => setAdvApprovedAmount(e.target.value)}
                            placeholder={req.amount}
                            style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #6EE7B7", fontSize: 14, fontWeight: 700, color: "#065F46", boxSizing: "border-box", marginBottom: 8 }}
                          />
                          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
                            Notes for employee <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
                          </label>
                          <textarea value={advApproveNote} onChange={e => setAdvApproveNote(e.target.value)}
                            placeholder="e.g. Approved ₹4000 — hotel rate adjusted per policy."
                            rows={2} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #D1D5DB", fontSize: 13, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
                        </div>
                      )}

                      {/* ── Decline panel ── */}
                      {isDeclineOpen && (
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
                            Reason for declining <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
                          </label>
                          <textarea value={advDeclineNote} onChange={e => setAdvDeclineNote(e.target.value)}
                            placeholder="e.g. Budget not available this month."
                            rows={2} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
                        </div>
                      )}

                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {req.canApprove === false ? (
                          <div style={{ fontSize: 13, color: "#92400e", background: "#FEF3C7", borderRadius: 8, padding: "8px 14px", fontWeight: 600 }}>
                            🔒 Awaiting the designated approver for this company
                          </div>
                        ) : !isDeclineOpen && !advApproveMode ? (
                          <>
                            <button disabled={isProcessing} onClick={() => { setAdvApproveMode(req.id); setAdvApprovedAmount(req.amount); setAdvApproveNote(""); }}
                              style={{ background: "#059669", color: "white", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                              ✅ Approve
                            </button>
                            <button disabled={isProcessing} onClick={() => { setAdvDeclineMode(req.id); setAdvDeclineNote(""); }}
                              style={{ background: "white", color: "#DC2626", border: "2px solid #DC2626", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                              ❌ Decline
                            </button>
                          </>
                        ) : advApproveMode === req.id ? (
                          <>
                            <button disabled={isProcessing} onClick={() => handleAdvApprove(req)}
                              style={{ background: isProcessing ? "#9ca3af" : "#059669", color: "white", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: isProcessing ? "not-allowed" : "pointer" }}>
                              {isProcessing ? "Processing..." : "Confirm Approval"}
                            </button>
                            <button disabled={isProcessing} onClick={() => { setAdvApproveMode(null); setAdvApprovedAmount(""); setAdvApproveNote(""); }}
                              style={{ background: "white", color: "#6b7280", border: "1px solid #D1D5DB", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button disabled={isProcessing} onClick={() => handleAdvDecline(req)}
                              style={{ background: isProcessing ? "#9ca3af" : "#DC2626", color: "white", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: isProcessing ? "not-allowed" : "pointer" }}>
                              {isProcessing ? "Processing..." : "Confirm Decline"}
                            </button>
                            <button disabled={isProcessing} onClick={() => { setAdvDeclineMode(null); setAdvDeclineNote(""); }}
                              style={{ background: "white", color: "#6b7280", border: "1px solid #D1D5DB", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Overage Approvals (actuals > projected) ────────────────────────────── */}
      {(isManager || isAdmin) && (
        <div className="card" style={{ marginBottom: 24, border: "2px solid #FCA5A5", background: "#FFF5F5" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 24 }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#991B1B", margin: 0 }}>
                Overage Approvals
                {overageTrips.length > 0 && (
                  <span style={{ marginLeft: 8, background: "#DC2626", color: "white", borderRadius: 20, padding: "2px 10px", fontSize: 13 }}>
                    {overageTrips.length}
                  </span>
                )}
              </h3>
              <p style={{ fontSize: 12, color: "#b91c1c", margin: 0, marginTop: 2 }}>Trips where actual expenses exceed the projected budget by more than 20%</p>
            </div>
          </div>

          {loadingOverage ? (
            <div style={{ textAlign: "center", padding: "20px 0", color: "#9ca3af" }}>Loading...</div>
          ) : overageTrips.length === 0 ? (
            <div style={{ textAlign: "center", padding: "16px 0", color: "#6b7280", fontSize: 14 }}>✅ No overage approvals required</div>
          ) : (
            <div style={{ display: "grid", gap: 14 }}>
              {overageTrips.map(trip => {
                const projected = parseFloat(trip.projected || 0);
                const actual    = parseFloat(trip.actual || 0);
                const overPct   = projected > 0 ? ((actual - projected) / projected * 100).toFixed(0) : 0;
                const isNoteOpen = overageNoteMode === trip.id;
                const isProcessing = overageProcessingId === trip.id;
                return (
                  <div key={trip.id} style={{
                    background: "white", borderRadius: 12, padding: "18px 20px",
                    border: "1.5px solid #FCA5A5", boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: "#1f2937" }}>{trip.title}</div>
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>👤 {trip.employee_name}</div>
                      </div>
                      <span style={{ background: "#FEE2E2", color: "#DC2626", borderRadius: 8, padding: "4px 12px", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                        +{overPct}% over budget
                      </span>
                    </div>

                    {/* Projected vs Actual bars */}
                    <div style={{ background: "#F9FAFB", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
                      {[
                        { label: "Projected", value: projected, color: "#059669", bg: "#D1FAE5" },
                        { label: "Actual",    value: actual,    color: "#DC2626", bg: "#FEE2E2" },
                      ].map(row => (
                        <div key={row.label} style={{ marginBottom: 8 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                            <span style={{ fontWeight: 600, color: "#6b7280" }}>{row.label}</span>
                            <span style={{ fontWeight: 800, color: row.color }}>₹{row.value.toLocaleString("en-IN")}</span>
                          </div>
                          <div style={{ height: 10, background: "#E5E7EB", borderRadius: 4, overflow: "hidden" }}>
                            <div style={{
                              height: "100%", width: `${Math.min((row.value / Math.max(projected, actual)) * 100, 100)}%`,
                              background: row.color, borderRadius: 4, transition: "width 0.5s",
                            }} />
                          </div>
                        </div>
                      ))}
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginTop: 4, paddingTop: 8, borderTop: "1px solid #E5E7EB" }}>
                        <span style={{ color: "#9ca3af" }}>Overage Amount</span>
                        <span style={{ fontWeight: 800, color: "#DC2626" }}>₹{(actual - projected).toLocaleString("en-IN")}</span>
                      </div>
                    </div>

                    {isNoteOpen && (
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
                          Approval Notes <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
                        </label>
                        <textarea
                          value={overageNote} onChange={e => setOverageNote(e.target.value)}
                          placeholder="e.g. Extra hotel night approved due to flight delay."
                          rows={2}
                          style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
                        />
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {!isNoteOpen ? (
                        <button onClick={() => { setOverageNoteMode(trip.id); setOverageNote(""); }}
                          style={{ background: "#059669", color: "white", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                          ✅ Approve Overage
                        </button>
                      ) : (
                        <>
                          <button disabled={isProcessing} onClick={() => handleApproveOverage(trip)}
                            style={{ background: isProcessing ? "#9ca3af" : "#059669", color: "white", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: isProcessing ? "not-allowed" : "pointer" }}>
                            {isProcessing ? "Processing..." : "Confirm Approval"}
                          </button>
                          <button onClick={() => { setOverageNoteMode(null); setOverageNote(""); }}
                            style={{ background: "white", color: "#6b7280", border: "1px solid #D1D5DB", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── BF Override Requests (manager + finance/admin) ───────────────────── */}
      {(isManager || isAdmin) && (
        <div className="card" style={{ marginBottom: 24, border: "2px solid #FDE68A", background: "#FFFBEB" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 24 }}>🍳</span>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#92400e", margin: 0 }}>
                Breakfast (BF) Override Requests
                {bfOverrides.length > 0 && (
                  <span style={{ marginLeft: 8, background: "#D97706", color: "white", borderRadius: 20, padding: "2px 10px", fontSize: 13 }}>
                    {bfOverrides.length}
                  </span>
                )}
              </h3>
              <p style={{ fontSize: 12, color: "#b45309", margin: 0, marginTop: 2 }}>
                Employee is claiming BF separately even though hotel bill includes breakfast
              </p>
            </div>
            {/* Select All toggle */}
            {approvableBf.length > 1 && !loadingBf && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#92400e", cursor: "pointer", userSelect: "none" }}>
                <input type="checkbox"
                  checked={selectedBf.size === approvableBf.length && approvableBf.length > 0}
                  onChange={e => setSelectedBf(e.target.checked ? new Set(approvableBf.map(r => r.id)) : new Set())}
                  style={{ width: 16, height: 16, cursor: "pointer" }} />
                Select All
              </label>
            )}
          </div>

          {loadingBf ? (
            <div style={{ textAlign: "center", padding: "20px 0", color: "#9ca3af" }}>Loading...</div>
          ) : bfOverrides.length === 0 ? (
            <div style={{ textAlign: "center", padding: "16px 0", color: "#6b7280", fontSize: 14 }}>✅ No pending BF override requests</div>
          ) : (
            <>
              <BulkBar
                selected={selectedBf} allItems={approvableBf}
                onSelectAll={() => setSelectedBf(new Set(approvableBf.map(r => r.id)))}
                onClearAll={() => { setSelectedBf(new Set()); setBulkBfDecline(false); setBulkBfNote(""); }}
                onBulkApprove={handleBulkBfApprove}
                onBulkDeclineClick={() => setBulkBfDecline(true)}
                showDeclineBox={bulkBfDecline}
                declineNote={bulkBfNote} onDeclineNoteChange={setBulkBfNote}
                onBulkDeclineConfirm={handleBulkBfDecline}
                onDeclineCancel={() => { setBulkBfDecline(false); setBulkBfNote(""); }}
                busy={bulkBfBusy} borderColor="#FDE68A"
                approveLabel="Approve Override" declineLabel="Decline"
              />
              <div style={{ display: "grid", gap: 14 }}>
                {bfOverrides.map(req => {
                  const isDeclineOpen = bfDeclineMode === req.id;
                  const isProcessing = bfProcessingId === req.id;
                  const isChecked = selectedBf.has(req.id);
                  return (
                    <div key={req.id} style={{
                      background: "white", borderRadius: 12, padding: "18px 20px",
                      border: isChecked ? "2px solid #D97706" : "1px solid #FDE68A",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                      transition: "border-color 0.15s"
                    }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                          {/* Checkbox */}
                          {req.canApprove !== false ? (
                            <input type="checkbox" checked={isChecked}
                              onChange={e => {
                                const s = new Set(selectedBf);
                                e.target.checked ? s.add(req.id) : s.delete(req.id);
                                setSelectedBf(s);
                              }}
                              style={{ width: 17, height: 17, marginTop: 3, cursor: "pointer", accentColor: "#D97706" }} />
                          ) : (
                            <div style={{ width: 17, marginTop: 3 }} />
                          )}
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: "#1f2937" }}>{req.employee_name}</div>
                            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{req.employee_email}</div>
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>📅 {fmtDate(req.created_at)}</div>
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>BF Amount</div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: "#D97706" }}>{fmtINR(req.amount)}</div>
                        </div>
                      </div>

                      <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "10px 14px", marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 16 }}>
                        <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Trip</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937", marginTop: 1 }}>{req.trip_title}</div></div>
                        <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Expense Date</div>
                          <div style={{ fontSize: 13, color: "#374151", marginTop: 1 }}>{new Date(req.expense_date).toLocaleDateString("en-IN")}</div></div>
                        <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Description</div>
                          <div style={{ fontSize: 13, color: "#374151", marginTop: 1 }}>{req.description}</div></div>
                        {(req.travel_from || req.travel_to) && (
                          <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Route</div>
                            <div style={{ fontSize: 13, color: "#374151", marginTop: 1 }}>📍 {req.travel_from || "?"} → {req.travel_to || "?"}</div></div>
                        )}
                      </div>

                      {/* Override reason */}
                      <div style={{ padding: "10px 14px", background: "#FEF3C7", borderRadius: 8, marginBottom: 14, fontSize: 13, color: "#92400e" }}>
                        <span style={{ fontWeight: 700 }}>Reason given: </span>{req.reason}
                      </div>

                      {isDeclineOpen && (
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
                            Reason for declining <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
                          </label>
                          <textarea value={bfDeclineNote} onChange={e => setBfDeclineNote(e.target.value)}
                            placeholder="e.g. Breakfast is included in hotel bill — cannot claim separately."
                            rows={2} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
                        </div>
                      )}

                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <ViewBillButton req={req} />
                        {req.canApprove === false ? (
                          <div style={{ fontSize: 13, color: "#92400e", background: "#FEF3C7", borderRadius: 8, padding: "8px 14px", fontWeight: 600 }}>
                            🔒 Awaiting the designated approver for this company
                          </div>
                        ) : !isDeclineOpen ? (
                          <>
                            <button disabled={isProcessing} onClick={() => handleBfApprove(req)}
                              style={{ background: isProcessing ? "#9ca3af" : "#059669", color: "white", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: isProcessing ? "not-allowed" : "pointer" }}>
                              {isProcessing ? "Processing..." : "✅ Approve Override"}
                            </button>
                            <button disabled={isProcessing} onClick={() => { setBfDeclineMode(req.id); setBfDeclineNote(""); }}
                              style={{ background: "white", color: "#DC2626", border: "2px solid #DC2626", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: isProcessing ? "not-allowed" : "pointer" }}>
                              ❌ Decline
                            </button>
                          </>
                        ) : (
                          <>
                            <button disabled={isProcessing} onClick={() => handleBfDecline(req)}
                              style={{ background: isProcessing ? "#9ca3af" : "#DC2626", color: "white", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: isProcessing ? "not-allowed" : "pointer" }}>
                              {isProcessing ? "Processing..." : "Confirm Decline"}
                            </button>
                            <button disabled={isProcessing} onClick={() => { setBfDeclineMode(null); setBfDeclineNote(""); }}
                              style={{ background: "white", color: "#6b7280", border: "1px solid #D1D5DB", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Cash Bill Approvals ──────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 24, border: "2px solid #FCA5A5", background: "#FFF5F5" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 24 }}>🧾</span>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#991B1B", margin: 0 }}>
              {isAdmin ? "Cash Voucher — All Approval Stages" : "Cash Voucher Approvals"}
              {cashApprovals.length > 0 && (
                <span style={{ marginLeft: 8, background: "#DC2626", color: "white", borderRadius: 20, padding: "2px 10px", fontSize: 13 }}>
                  {cashApprovals.length}
                </span>
              )}
            </h3>
            <p style={{ fontSize: 12, color: "#b91c1c", margin: 0, marginTop: 2 }}>
              {isAdmin
                ? "Monitor every stage; approve for an unavailable manager or complete Finance review"
                : "Ebekmed cash vouchers and configured exceptions require manager approval"}
            </p>
          </div>
          {/* Select All toggle */}
          {approvableCash.length > 1 && !loadingCash && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#991B1B", cursor: "pointer", userSelect: "none" }}>
              <input type="checkbox"
                checked={selectedCash.size === approvableCash.length && approvableCash.length > 0}
                onChange={e => setSelectedCash(e.target.checked ? new Set(approvableCash.map(r => r.id)) : new Set())}
                style={{ width: 16, height: 16, cursor: "pointer" }} />
              Select All
            </label>
          )}
        </div>

        {loadingCash ? (
          <div style={{ textAlign: "center", padding: "20px 0", color: "#9ca3af" }}>Loading...</div>
        ) : cashApprovals.length === 0 ? (
          <div style={{ textAlign: "center", padding: "16px 0", color: "#6b7280", fontSize: 14 }}>
            ✅ No pending cash bill approvals
          </div>
        ) : (
          <>
            <BulkBar
              selected={selectedCash} allItems={approvableCash}
              onSelectAll={() => setSelectedCash(new Set(approvableCash.map(r => r.id)))}
              onClearAll={() => { setSelectedCash(new Set()); setBulkCashDecline(false); setBulkCashNote(""); }}
              onBulkApprove={handleBulkCashApprove}
              onBulkDeclineClick={() => setBulkCashDecline(true)}
              showDeclineBox={bulkCashDecline}
              declineNote={bulkCashNote} onDeclineNoteChange={setBulkCashNote}
              onBulkDeclineConfirm={handleBulkCashDecline}
              onDeclineCancel={() => { setBulkCashDecline(false); setBulkCashNote(""); }}
              busy={bulkCashBusy} borderColor="#FCA5A5"
              approveLabel={isAdmin ? "Approve Current Stage" : "Approve"} declineLabel="Decline Current Stage"
            />
            <div style={{ display: "grid", gap: 14 }}>
              {cashApprovals.map(req => {
                const isDeclineOpen = cashDeclineMode === req.id;
                const isProcessing = cashProcessingId === req.id;
                const isChecked = selectedCash.has(req.id);
                return (
                  <div key={req.id} style={{
                    background: "white", borderRadius: 12, padding: "18px 20px",
                    border: isChecked ? "2px solid #DC2626" : "1px solid #FECACA",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    transition: "border-color 0.15s"
                  }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                        {/* Checkbox */}
                        {req.canApprove !== false ? (
                          <input type="checkbox" checked={isChecked}
                            onChange={e => {
                              const s = new Set(selectedCash);
                              e.target.checked ? s.add(req.id) : s.delete(req.id);
                              setSelectedCash(s);
                            }}
                            style={{ width: 17, height: 17, marginTop: 3, cursor: "pointer", accentColor: "#DC2626" }} />
                        ) : (
                          <div style={{ width: 17, marginTop: 3 }} />
                        )}
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: "#1f2937" }}>{req.employee_name}</div>
                          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{req.employee_email}</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>📅 {fmtDate(req.created_at)}</div>
                          {isAdmin && (
                            <div style={{ marginTop: 6, fontSize: 12, padding: "3px 8px", background: "#D1FAE5", color: "#065F46", borderRadius: 6, display: "inline-block", fontWeight: 600 }}>
                              {req.manager_status === "pending" ? "⏳ Manager Pending"
                                : req.manager_status === "declined" ? "❌ Manager Declined"
                                : req.finance_status === "pending" ? "✅ Manager Approved · Finance Pending"
                                : req.finance_status === "approved" ? "✅ Fully Approved"
                                : "❌ Finance Declined"}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase" }}>
                          {req.category === "Catering Charges" ? "Catering Claim Amount" : "Cash Voucher Amount"}
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: "#DC2626" }}>{fmtINR(req.amount)}</div>
                        {/* The cap is on the day as a whole, so show what the
                            employee's cash vouchers for that date come to. */}
                        {req.cash_voucher_day_total != null && (
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                            {fmtINR(req.cash_voucher_day_total)} total for {new Date(req.expense_date).toLocaleDateString("en-IN")}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "10px 14px", marginBottom: 14, display: "flex", flexWrap: "wrap", gap: 16 }}>
                      <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Trip</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937", marginTop: 1 }}>{req.trip_title}</div></div>
                      <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Category</div>
                        <div style={{ fontSize: 14, color: "#374151", marginTop: 1 }}>{req.category}</div></div>
                      <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Description</div>
                        <div style={{ fontSize: 13, color: "#374151", marginTop: 1 }}>{req.description}</div></div>
                      <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Date</div>
                        <div style={{ fontSize: 13, color: "#374151", marginTop: 1 }}>{new Date(req.expense_date).toLocaleDateString("en-IN")}</div></div>
                      {(req.travel_from || req.travel_to) && (
                        <div><div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Route</div>
                          <div style={{ fontSize: 13, color: "#374151", marginTop: 1 }}>📍 {req.travel_from || "?"} → {req.travel_to || "?"}</div></div>
                      )}
                      {req.category === "Catering Charges" && (
                        <>
                          <div><div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase" }}>Headcount</div>
                            <div style={{ fontSize: 13, color: "#1f2937", marginTop: 1 }}>
                              Candidates {req.catering_candidate_count ?? 0} · Ebek staff {req.catering_ebek_staff_count ?? 0} · Support staff {req.catering_support_staff_count ?? 0}
                            </div></div>
                          <div><div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase" }}>Ordered Packs</div>
                            <div style={{ fontSize: 13, color: "#1f2937", marginTop: 1 }}>
                              Breakfast {req.catering_bf_packs ?? 0} · Lunch {req.catering_lunch_packs ?? 0} · Snacks {req.catering_snacks_packs ?? 0}
                            </div></div>
                          <div><div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase" }}>Proof</div>
                            <div style={{ fontSize: 13, color: "#1f2937", marginTop: 1 }}>{req.bill_type === "bill" ? "Original bill" : "Cash voucher"}</div></div>
                        </>
                      )}
                    </div>

                    {isDeclineOpen && (
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
                          Reason for declining <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
                        </label>
                        <textarea value={cashDeclineNote} onChange={e => setCashDeclineNote(e.target.value)}
                          placeholder="e.g. Please get a proper printed receipt."
                          rows={2} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <ViewBillButton req={req} />
                      {req.manager_status === "approved" && req.finance_status === "approved" ? (
                        <div style={{ fontSize: 13, color: "#065F46", background: "#D1FAE5", borderRadius: 8, padding: "8px 14px", fontWeight: 700 }}>
                          ✅ No action required — manager and Finance approved
                        </div>
                      ) : req.manager_status === "declined" || req.finance_status === "declined" ? (
                        <div style={{ fontSize: 13, color: "#991B1B", background: "#FEE2E2", borderRadius: 8, padding: "8px 14px", fontWeight: 700 }}>
                          ❌ This voucher was declined
                        </div>
                      ) : req.canApprove === false ? (
                        <div style={{ fontSize: 13, color: "#991B1B", background: "#FEE2E2", borderRadius: 8, padding: "8px 14px", fontWeight: 600 }}>
                          🔒 Awaiting the designated approver for this company
                        </div>
                      ) : !isDeclineOpen ? (
                        <>
                          <button disabled={isProcessing} onClick={() => handleCashApprove(req)}
                            style={{ background: isProcessing ? "#9ca3af" : "#059669", color: "white", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: isProcessing ? "not-allowed" : "pointer" }}>
                            {isProcessing ? "Processing..." : isAdmin
                              ? (req.manager_status === "pending" ? "✅ Approve for Manager" : "✅ Finance Approve")
                              : "✅ Approve"}
                          </button>
                          <button disabled={isProcessing} onClick={() => { setCashDeclineMode(req.id); setCashDeclineNote(""); }}
                            style={{ background: "white", color: "#DC2626", border: "2px solid #DC2626", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: isProcessing ? "not-allowed" : "pointer" }}>
                            ❌ Decline
                          </button>
                        </>
                      ) : (
                        <>
                          <button disabled={isProcessing} onClick={() => handleCashDecline(req)}
                            style={{ background: isProcessing ? "#9ca3af" : "#DC2626", color: "white", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: isProcessing ? "not-allowed" : "pointer" }}>
                            {isProcessing ? "Processing..." : "Confirm Decline"}
                          </button>
                          <button disabled={isProcessing} onClick={() => { setCashDeclineMode(null); setCashDeclineNote(""); }}
                            style={{ background: "white", color: "#6b7280", border: "1px solid #D1D5DB", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
